import { mapFromStringValue, mapFromStringValues, mapToStringValue } from 'mappings/stringValueMapping';
import StringValueDTO from 'models/dto/stringValue/stringValueDTO';
import StringValueMergeDTO from 'models/dto/stringValue/stringValueMergeDTO';
import StringValueUsageDTO from 'models/dto/stringValue/stringValueUsageDTO';
import StringValue, { StringValueTypes } from 'models/stringValue/stringValue';
import AppError from 'utils/appError';
import http from './httpService';

export async function apiGetStringValues(type: StringValueTypes, accessToken: string): Promise<StringValue[]> {
  try {
    const ar = await http.get<StringValueDTO[]>(`/stringvalues/${type}`, http.getRequestConfig(accessToken));

    return mapFromStringValues(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//
// Several components can ask for the same string value list at the same moment, for example one
// quick filter chip per selected value. Share the in-flight request between them so that only one
// API call is made. The entry is removed as soon as the request settles, so a later caller always
// gets fresh data.
//
// The key carries the request scope next to the type: string values are per tenant, and switching
// org unit changes that scope in place (globalOrgUnitId) without reloading the app. Without the
// scope in the key, a component rendering in the new org could join a request made for the old one.
//
const inFlightStringValues = new Map<string, Promise<StringValue[]>>();

export function apiGetStringValuesShared(type: StringValueTypes, accessToken: string): Promise<StringValue[]> {
  const cacheKey = `${http.getRequestScopeKey()}|${type}`;
  const pendingRequest = inFlightStringValues.get(cacheKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = apiGetStringValues(type, accessToken).finally(() => {
    inFlightStringValues.delete(cacheKey);
  });
  inFlightStringValues.set(cacheKey, request);

  return request;
}

export async function apiGetStringValuesForTypes(
  types: StringValueTypes[],
  accessToken: string,
): Promise<StringValue[]> {
  try {
    const typeQuery = types.map((type) => `types=${type}`).join('&');
    const ar = await http.get<StringValueDTO[]>(`/stringvalues?${typeQuery}`, http.getRequestConfig(accessToken));

    return mapFromStringValues(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetStringValueUsageCounts(
  type: StringValueTypes,
  accessToken: string,
): Promise<StringValueUsageDTO[]> {
  try {
    const ar = await http.get<StringValueUsageDTO[]>(
      `/stringvalues/${type}/usagecounts`,
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetStringValueUsageCountsForTypes(
  types: StringValueTypes[],
  accessToken: string,
): Promise<StringValueUsageDTO[]> {
  try {
    const typeQuery = types.map((type) => `types=${type}`).join('&');
    const ar = await http.get<StringValueUsageDTO[]>(
      `/stringvalues/usagecounts?${typeQuery}`,
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddStringValue(values: StringValue, accessToken: string): Promise<StringValue> {
  try {
    const valueDTO = mapToStringValue(values);
    const ar = await http.post<StringValueDTO>(`/stringvalues`, valueDTO, http.getRequestConfig(accessToken));

    return mapFromStringValue(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateStringValues(values: StringValue[], accessToken: string): Promise<StringValue[]> {
  try {
    const valueDTOs = values.map((v) => mapToStringValue(v));
    const ar = await http.put<StringValueDTO[]>(`/stringvalues/bulk`, valueDTOs, http.getRequestConfig(accessToken));

    return mapFromStringValues(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiMergeStringValues(
  sourceStringValues: StringValue[],
  targetStringValue: StringValue,
  accessToken: string,
): Promise<void> {
  try {
    const mergeDTO = new StringValueMergeDTO();
    mergeDTO.sourceStringValues = sourceStringValues.map((v) => mapToStringValue(v));
    mergeDTO.targetStringValue = mapToStringValue(targetStringValue);

    await http.post<void>(`/stringvalues/merge`, mergeDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteStringValue(value: StringValue, accessToken: string): Promise<void> {
  try {
    await http.post<StringValueDTO[]>(
      `/stringvalues/delete/${value.stringValueId}`,
      null,
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
